<script setup lang="ts">
import { useConfigStore } from '@core/stores/config'
import { ref } from 'vue'
import Footer from '@/views/front-pages/front-page-footer.vue'
import Navbar from '@/views/front-pages/front-page-navbar.vue'
import BlogPreviewSection from '@/views/front-pages/landing-page/blog-preview-section.vue'
import BookingHeroSection from '@/views/front-pages/landing-page/booking-hero-section.vue'
import CampsPreviewSection from '@/views/front-pages/landing-page/camps-preview-section.vue'
import FaqSection from '@/views/front-pages/landing-page/faq-section.vue'
import OurTeam from '@/views/front-pages/landing-page/monitors.vue'
import AboutUsTeam from '@/views/front-pages/landing-page/over-tspeelfabrik.vue'

const store = useConfigStore()

store.skin = 'default'
definePageMeta({
  layout: 'blank',
  public: true,
})

// SEO Configuration
const { setSeoMeta, getOrganizationSchema, getLocalBusinessSchema } = useSeo()

setSeoMeta({
  title: 'Kinderkampen',
  description: '\'t SpeelFabrik organiseert onvergetelijke kampen voor kinderen en jongeren. Ontdek onze zomerkampen, sportkampen en creatieve kampen. Boek nu je plekje!',
  path: '/',
  image: '/images/og-home.jpg',
  keywords: 'kinderkampen, zomerkampen, sportkampen, kinderen, jongeren, Zemst, speelfabrik, vakantiekampen, jeugdkampen',
})

// Branded OG image with custom background
defineOgImage('SpeelFabrik', {
  title: 'Kinderkampen',
  description: '\'t SpeelFabrik organiseert onvergetelijke kampen voor kinderen en jongeren. Ontdek onze zomerkampen, sportkampen en creatieve activiteiten.',
  backgroundImage: '/images/ontdek.png',
})

// Structured data for Organization and LocalBusiness
useSchemaOrg([
  getOrganizationSchema(),
  getLocalBusinessSchema(),
])

const activeSectionId = ref()

const refHome = ref()
const refCamps = ref()
const refMonitoren = ref()
const refFaq = ref()

useIntersectionObserver(
  [refHome, refCamps, refMonitoren, refFaq],
  ([entry]) => {
    if (entry?.isIntersecting && entry.target)
      activeSectionId.value = entry.target.id
  },
  {
    threshold: 0.25,
  },
)
</script>

<template>
  <div class="landing-page-wrapper">
    <Navbar :active-id="activeSectionId" />

    <!-- 👉 Hero Section with Search  -->
    <BookingHeroSection ref="refHome" />

    <!-- 👉 Wave Transition -->
    <div class="wave-transition">
      <svg viewBox="0 0 1200 180" preserveAspectRatio="none" class="wave-svg wave-1">
        <path d="M0,0 L0,0 L1200,0 L1200,60 C1000,140 800,20 600,100 C400,180 200,40 0,120 Z" />
      </svg>
      <svg viewBox="0 0 1200 180" preserveAspectRatio="none" class="wave-svg wave-2">
        <path d="M0,0 L0,0 L1200,0 L1200,80 C950,20 750,140 500,60 C300,160 150,30 0,100 Z" />
      </svg>
      <svg viewBox="0 0 1200 180" preserveAspectRatio="none" class="wave-svg wave-3">
        <path d="M0,0 L0,0 L1200,0 L1200,100 C1050,30 850,150 600,70 C350,10 150,130 0,60 Z" />
      </svg>
    </div>

    <!-- 👉 About 't SpeelFabrik Section -->
    <!-- 👉 Team section -->
    <AboutUsTeam />

    <!-- 👉 Wave Transition to Red Section -->
    <div class="wave-transition-red-top">
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="wave-svg-red wave-red-1">
        <path d="M0,0 L0,0 L1200,0 L1200,60 C1000,100 800,20 600,70 C400,120 200,40 0,80 Z" />
      </svg>
      <svg viewBox="0 0 1200 120" preserveAspectRatio="none" class="wave-svg-red wave-red-2">
        <path d="M0,0 L0,0 L1200,0 L1200,50 C950,10 750,90 500,40 C300,100 150,20 0,60 Z" />
      </svg>
    </div>

    <!-- 👉 Camps Preview Section -->
    <CampsPreviewSection ref="refCamps" />

    <!-- 👉 Wave Transition from Red Section -->
    <div class="wave-transition-red-bottom">
      <svg viewBox="0 0 1200 140" preserveAspectRatio="none" class="wave-svg-red-bottom wave-red-bottom-1">
        <path d="M0,60 C200,20 400,100 600,50 C800,0 1000,80 1200,40 L1200,140 L0,140 Z" />
      </svg>
      <svg viewBox="0 0 1200 140" preserveAspectRatio="none" class="wave-svg-red-bottom wave-red-bottom-2">
        <path d="M0,40 C150,90 300,10 500,60 C750,110 950,30 1200,70 L1200,140 L0,140 Z" />
      </svg>
    </div>

    <!-- 👉 Our Team -->
    <div id="monitoren" ref="refMonitoren" :style="{ 'background-color': 'rgb(var(--v-theme-surface))' }">
      <OurTeam />
    </div>

    <!-- 👉 Blog/News Preview Section -->
    <BlogPreviewSection />

    <!-- 👉 FAQ Section -->
    <FaqSection ref="refFaq" />

    <!-- 👉 Footer -->
    <Footer />
  </div>
</template>

<style lang="scss">
.wave-transition {
  position: relative;
  z-index: 5;
  overflow: hidden;
  block-size: 180px;
  margin-block-start: -60px;

  .wave-svg {
    position: absolute;
    block-size: 100%;
    inline-size: 110%;
    inset-block-start: 0;
    inset-inline-start: -5%;

    path {
      fill: rgb(var(--v-theme-surface));
    }
  }

  .wave-1 {
    animation: wave-float-1 8s ease-in-out infinite;
    opacity: 0.4;
  }

  .wave-2 {
    animation: wave-float-2 6s ease-in-out infinite;
    opacity: 0.6;
  }

  .wave-3 {
    animation: wave-float-3 10s ease-in-out infinite;
    opacity: 0.9;
  }
}

@keyframes wave-float-1 {
  0%,
  100% {
    transform: translateY(0) scaleX(1);
  }

  50% {
    transform: translateY(-8px) scaleX(1.02);
  }
}

@keyframes wave-float-2 {
  0%,
  100% {
    transform: translateY(0) scaleX(1);
  }

  50% {
    transform: translateY(-12px) scaleX(1.01);
  }
}

@keyframes wave-float-3 {
  0%,
  100% {
    transform: translateY(0) scaleX(1);
  }

  50% {
    transform: translateY(-5px) scaleX(1.03);
  }
}

.wave-transition-red-top {
  position: relative;
  z-index: 5;
  overflow: hidden;
  background-color: #f26f63;
  block-size: 120px;
  margin-block-end: -1px;

  .wave-svg-red {
    position: absolute;
    block-size: 100%;
    inline-size: 110%;
    inset-block-start: 0;
    inset-inline-start: -5%;

    path {
      fill: rgb(var(--v-theme-background));
    }
  }

  .wave-red-1 {
    animation: wave-float-1 7s ease-in-out infinite;
    opacity: 0.7;
  }

  .wave-red-2 {
    animation: wave-float-2 9s ease-in-out infinite;
    opacity: 0.9;
  }
}

.wave-transition-red-bottom {
  position: relative;
  z-index: 5;
  overflow: hidden;
  background-color: #f26f63;
  block-size: 140px;
  margin-block-start: -1px;

  .wave-svg-red-bottom {
    position: absolute;
    block-size: 100%;
    inline-size: 110%;
    inset-block-start: 0;
    inset-inline-start: -5%;

    path {
      fill: rgb(var(--v-theme-surface));
    }
  }

  .wave-red-bottom-1 {
    animation: wave-float-1 8s ease-in-out infinite;
    opacity: 0.7;
  }
}

.camps-preview-section {
  position: relative;
  z-index: 1;
  overflow: hidden;
  background-color: rgb(var(--v-theme-background));

  .gear-decoration {
    position: absolute;
    z-index: 0;
    background-image: url("/tandwiel.svg");
    background-repeat: no-repeat;
    background-size: contain;
    opacity: 0.08;
    pointer-events: none;
  }

  .gear-1 {
    animation: rotate-slow 30s linear infinite;
    block-size: 180px;
    inline-size: 180px;
    inset-block-start: 10%;
    inset-inline-start: 5%;
  }

  .gear-2 {
    animation: rotate-slow-reverse 25s linear infinite;
    block-size: 120px;
    inline-size: 120px;
    inset-block-start: 60%;
    inset-inline-start: 8%;
  }

  .gear-3 {
    animation: rotate-slow 35s linear infinite;
    block-size: 200px;
    inline-size: 200px;
    inset-block-start: 15%;
    inset-inline-end: 8%;
  }

  .gear-4 {
    animation: rotate-slow-reverse 28s linear infinite;
    block-size: 140px;
    inline-size: 140px;
    inset-block-end: 10%;
    inset-inline-end: 5%;
  }

  .v-container {
    position: relative;
    z-index: 1;
  }

  .camps-info-container {
    position: sticky;
    inset-block-start: 100px;
  }

  .camp-preview-card {
    border: 1px solid rgba(var(--v-theme-on-surface), 0.08);
    border-radius: 12px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 8px 24px rgba(0, 0, 0, 12%) !important;
      transform: translateX(4px);
    }
  }
}

.about-section {
  .about-icon-container {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    border-radius: 50%;
    background: linear-gradient(135deg, rgba(var(--v-theme-primary), 0.1) 0%, rgba(var(--v-theme-primary), 0.05) 100%);

    .about-main-icon {
      animation: float 3s ease-in-out infinite;
    }
  }

  .about-content {
    p {
      line-height: 1.8;
    }
  }

  .feature-card {
    border-radius: 12px;
    transition: all 0.3s ease;

    &:hover {
      box-shadow: 0 12px 24px rgba(0, 0, 0, 15%) !important;
      transform: translateY(-8px);
    }
  }
}

@keyframes float {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-15px);
  }
}

@keyframes rotate-slow {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}

@keyframes rotate-slow-reverse {
  0% {
    transform: rotate(360deg);
  }

  100% {
    transform: rotate(0deg);
  }
}

@media (max-width: 960px) and (min-width: 600px) {
  .landing-page-wrapper {
    .v-container {
      padding-inline: 2rem !important;
    }
  }

  .wave-transition {
    block-size: 150px;
  }

  .search-filter-section {
    margin-block-start: -240px;
  }
}

@media (max-width: 600px) {
  .wave-transition {
    block-size: 120px;
  }

  .search-filter-section {
    margin-block-start: -200px;
  }
}
</style>
